import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Table from "../../../components/Common/Table/index";
import { fetchMarketingPersonBusinessReportDetail } from "services/marketingPersonBusinessReport";
import { DETAIL_COLUMNS } from "./config";
import Loader from "components/Common/loader";
import usePagination from "hooks/usePagination";
import { STALE_TIME } from "utils/constants";
import { useCallback } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import formatDate from "utils/formateDate";

const MarketingPersonBusinessReportDetail = () => {
  const [searchParams] = useSearchParams();

  const employee_code_id = searchParams.get("employee_code_id");
  const date_from = searchParams.get("date_from");
  const date_to = searchParams.get("date_to");
  const is_active = searchParams.get("is_active");

  const {
    currentPage,
    itemPerPage,
    disablePrev,
    disableNext,
    disableFirst,
    disableLast,
    totalPages,
    updateCurrentPage,
    updateTotalRecords,
  } = usePagination();

  const { data, isLoading } = useQuery(
    ["detail", employee_code_id, date_from, date_to, is_active, currentPage],
    () =>
      fetchMarketingPersonBusinessReportDetail({
        employee_code_id,
        date_from,
        date_to,
        is_active,
        page_no: currentPage,
        limit: itemPerPage,
      }),
    {
      enabled: !!employee_code_id,
      staleTime: STALE_TIME,
      onSuccess: (res) => {
        updateTotalRecords(res?.data?.additional_data?.total_count || 0);
      },
    },
  );

  const loadNextPage = useCallback(() => {
    updateCurrentPage(currentPage + 1);
  }, [currentPage, updateCurrentPage]);

  const loadPreviousPage = useCallback(() => {
    updateCurrentPage(currentPage - 1);
  }, [currentPage, updateCurrentPage]);

  const loadFirstPage = useCallback(() => {
    if (currentPage !== 1) updateCurrentPage(1);
  }, [currentPage, updateCurrentPage]);

  const loadLastPage = useCallback(() => {
    if (currentPage !== totalPages) updateCurrentPage(totalPages);
  }, [currentPage, totalPages, updateCurrentPage]);

  const exportToPDF = () => {
    const tableData = data?.data?.data || [];

    if (!tableData.length) {
      alert("No data to export");
      return;
    }

    const doc = new jsPDF();

    doc.setFontSize(16);
    doc.setFont(undefined, "bold");
    doc.text("Marketing Person Business Report Detail", 105, 15, {
      align: "center",
    });

    const fromDate = formatDate(date_from);
    const toDate = formatDate(date_to);

    doc.setFontSize(11);
    doc.setFont(undefined, "normal");

    doc.text(`Date Range: ${fromDate} to ${toDate}`, 105, 22, {
      align: "center",
    });

    doc.line(14, 25, 196, 25);

    const tableColumn = [
      "S.No",
      "Franchise Code",
      "Company Name",
      "Marketing Person",
      "Total Booking Amount",
    ];

    const tableRows = tableData.map((row, index) => [
      index + 1,
      row.franchise_code,
      row.company_name,
      row.marketing_person_obj,
      `${row.total_booking_amount || 0}`,
    ]);

    autoTable(doc, {
      startY: 30,
      head: [tableColumn],
      body: tableRows,
    });

    doc.save("marketing-report-detail.pdf");
  };

  const exportToCSV = () => {
    const tableData = data?.data?.data || [];

    if (!tableData.length) {
      alert("No data to export");
      return;
    }

    const fromDate = formatDate(date_from);
    const toDate = formatDate(date_to);

    let csvContent = "";

    csvContent += `"Marketing Person Business Report Detail"\n`;
    csvContent += `"Date Range: ${fromDate} to ${toDate}"\n\n`;

    const headers = [
      "S.No",
      "Franchise Code",
      "Company Name",
      "Marketing Person",
      "Total Booking Amount",
    ];

    const rows = tableData.map((row, index) => [
      index + 1,
      row.franchise_code,
      row.company_name,
      row.marketing_person_obj,
      row.total_booking_amount || 0,
    ]);

    csvContent +=
      headers.join(",") + "\n" + rows.map((e) => e.join(",")).join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "marketing-report-detail.csv";
    link.click();
  };

  if (isLoading) return <Loader isLoading={true} />;

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h2>Marketing Person Business Report Detail</h2>

        <div>
          <button className="btn btn-success me-2" onClick={exportToCSV}>
            Download CSV
          </button>
          <button className="btn btn-danger" onClick={exportToPDF}>
            Download PDF
          </button>
        </div>
      </div>

      <Table
        data={data?.data?.data || []}
        tableColumns={DETAIL_COLUMNS({})}
        currentPage={currentPage}
        totalPages={totalPages}
        itemPerPage={itemPerPage}
        disablePrev={disablePrev}
        disableNext={disableNext}
        disableFirst={disableFirst}
        disableLast={disableLast}
        loadFirstPage={loadFirstPage}
        loadLastPage={loadLastPage}
        loadNextPage={loadNextPage}
        loadPreviousPage={loadPreviousPage}
        totalRecords={data?.data?.additional_data?.total_count || 0}
      />
    </div>
  );
};

export default MarketingPersonBusinessReportDetail;
